import React, { useEffect, useRef, useState } from 'react';
import dropin from 'braintree-web-drop-in';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Icons } from './Icons';
import { formatPlanPrice, formatPrice } from '../utils/helpers';
import { track } from '../utils/analytics';

export default function UpgradeModal({ onClose }) {
  const { getClientToken, subscribe, validateCoupon } = useSubscription();
  const containerRef = useRef(null);
  const dropinRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [planPrice, setPlanPrice] = useState(null);
  const [planCurrency, setPlanCurrency] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [checkingCoupon, setCheckingCoupon] = useState(false);

  // Runs once on mount only — getClientToken/subscribe are recreated on every
  // SubscriptionContext render, and re-running this would tear down and
  // recreate the Drop-in form (and its iframes) underneath the user.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getClientToken();
        if (cancelled) return;
        setPlanPrice(data.planPrice || null);
        setPlanCurrency(data.planCurrency || null);
        const instance = await dropin.create({
          authorization: data.clientToken,
          container: containerRef.current,
        });
        if (cancelled) { instance.teardown().catch(() => {}); return; }
        dropinRef.current = instance;
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError('Could not load the payment form. Please try again.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (dropinRef.current) dropinRef.current.teardown().catch(() => {});
    };
    // eslint-disable-line
  }, []);

  // A mistyped code is an ordinary thing, so it reports under the field rather
  // than in the error banner the payment itself uses.
  const handleApplyCoupon = async () => {
    const code = couponInput.trim();
    if (!code || checkingCoupon) return;
    setCheckingCoupon(true);
    setCouponError('');
    try {
      const { data } = await validateCoupon(code);
      if (data.valid) {
        setCoupon(data);
        setCouponInput(data.code);
      } else {
        setCoupon(null);
        setCouponError("That code isn't valid.");
      }
    } catch (e) {
      setCoupon(null);
      setCouponError('Could not check that code. Please try again.');
    }
    setCheckingCoupon(false);
  };

  const handleRemoveCoupon = () => {
    setCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  const handleSubscribe = async () => {
    if (!dropinRef.current) return;
    setSubmitting(true);
    setError('');
    try {
      const { nonce } = await dropinRef.current.requestPaymentMethod();
      // The applied code, not whatever is currently typed — an edited-but-not
      // applied field must not quietly change what gets charged.
      await subscribe(nonce, coupon?.code);
      setSuccess(true);
      // The effective price, not the plan price. A coupon that halves the first
      // three months makes those months worth half — recording the sticker
      // price would overstate revenue by exactly the discount that was the
      // reason the customer converted.
      track('subscription_started', {
        value: Number(coupon?.discountedPrice ?? planPrice) || 0,
        currency: coupon?.planCurrency || planCurrency || 'USD',
        coupon: coupon?.code || 'none',
      });
    } catch (e) {
      setError(e?.message || 'Could not process your subscription. Please check your payment details and try again.');
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(44,36,24,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        width: 480, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto',
        background: '#FDFBF7', borderRadius: 20,
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          background: '#2C2418', padding: '24px 28px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: '#C4975A', display: 'flex' }}><Icons.Star size={22} /></span>
            <h2 style={{ margin: 0, color: '#F5EDE3', fontSize: 20, fontFamily: "'Crimson Pro', Georgia, serif", fontWeight: 700 }}>
              {success ? 'Welcome to Kinship Trees Plus' : 'Upgrade to Continue'}
            </h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#A0937F', cursor: 'pointer', display: 'flex' }}>
            <Icons.Close size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px 28px' }}>
          {success ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ margin: '0 0 8px', fontSize: 14, color: '#2C2418', fontWeight: 600 }}>You're subscribed!</p>
              <p style={{ margin: '0 0 24px', fontSize: 13, color: '#6B5E4F', lineHeight: 1.6 }}>
                You now have room for up to 1,000 people and 100GB of storage.
              </p>
              <button
                onClick={onClose}
                style={{
                  padding: '12px 28px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #C4975A 0%, #A07840 100%)',
                  color: '#2C2418', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <div style={{ background: '#F5EDE3', borderRadius: 12, padding: '14px 16px', border: '1.5px solid #E8D8C0', marginBottom: 20 }}>
                <p style={{ margin: 0, fontSize: 13, color: '#6B5E4F', lineHeight: 1.55 }}>
                  Up to <strong style={{ color: '#2C2418' }}>1,000 people</strong> and{' '}
                  <strong style={{ color: '#2C2418' }}>100GB</strong> of storage — plenty of room
                  for photos, documents, and your full family history.
                </p>
                {planPrice && (
                  <p style={{ margin: '8px 0 0', fontSize: 13, color: '#C4975A', fontWeight: 700 }}>
                    {coupon ? (
                      <>
                        <span style={{ color: '#8B7D6B', fontWeight: 500, textDecoration: 'line-through', marginRight: 6 }}>
                          {formatPrice(planPrice, planCurrency)}
                        </span>
                        {formatPlanPrice(coupon.discountedPrice, coupon.planCurrency || planCurrency)}
                      </>
                    ) : formatPlanPrice(planPrice, planCurrency)}
                  </p>
                )}
                {coupon && (
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6B5E4F' }}>
                    {coupon.name || coupon.code} applied
                    {coupon.numberOfBillingCycles
                      ? ` — first ${coupon.numberOfBillingCycles === 1 ? 'month' : `${coupon.numberOfBillingCycles} months`}`
                      : ''}
                  </p>
                )}
              </div>

              {/* Outside the Drop-in container below: that mount point is
                  Braintree's iframe and cannot hold our fields. */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#6B5E4F', fontWeight: 600, marginBottom: 6 }}>
                  Have a code?
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={couponInput}
                    onChange={(e) => { setCouponInput(e.target.value); setCouponError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyCoupon(); } }}
                    disabled={!!coupon || submitting}
                    placeholder="Enter code"
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: 8,
                      border: `1.5px solid ${couponError ? '#C0392B' : '#E8D8C0'}`,
                      background: coupon ? '#F5EDE3' : '#FFFFFF',
                      fontSize: 13, color: '#2C2418', fontFamily: "'DM Sans', sans-serif",
                    }}
                  />
                  <button
                    onClick={coupon ? handleRemoveCoupon : handleApplyCoupon}
                    disabled={submitting || checkingCoupon || (!coupon && !couponInput.trim())}
                    style={{
                      padding: '10px 16px', borderRadius: 8, border: '1.5px solid #C4975A',
                      background: 'transparent', color: '#A07840', fontSize: 13, fontWeight: 700,
                      cursor: submitting || checkingCoupon ? 'default' : 'pointer',
                      opacity: submitting || checkingCoupon || (!coupon && !couponInput.trim()) ? 0.5 : 1,
                      fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
                    }}
                  >
                    {checkingCoupon ? 'Checking…' : coupon ? 'Remove' : 'Apply'}
                  </button>
                </div>
                {couponError && (
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: '#C0392B' }}>{couponError}</p>
                )}
              </div>

              {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}>
                  <div style={{
                    width: 28, height: 28, border: '3px solid #E8E0D4',
                    borderTop: '3px solid #C4975A', borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                </div>
              )}

              <div ref={containerRef} style={{ display: loading ? 'none' : 'block' }} />

              {error && (
                <p style={{ margin: '12px 0 0', fontSize: 12.5, color: '#C0392B' }}>{error}</p>
              )}

              {!loading && (
                <button
                  onClick={handleSubscribe}
                  disabled={submitting}
                  style={{
                    width: '100%', marginTop: 20, padding: '13px 0', borderRadius: 10, border: 'none',
                    background: 'linear-gradient(135deg, #C4975A 0%, #A07840 100%)',
                    color: '#2C2418', fontSize: 14, fontWeight: 700,
                    cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1,
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  {submitting ? 'Processing…' : 'Subscribe'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
